// PageAgentPane — the page helper's pane (Douglas plan T8).
//
// The agent beside a page: an ordinary agent chat (usePageAgent creates it with
// the page's profile and sends the page's tools with every message), drawn by
// SidePaneChat's `conversation` input, so its tool chains, thinking, ask_user
// cards, tables and artifacts look as they do in the main list. What the pane
// adds:
//  - a Confirm/Decline card above the composer for each page call the page
//    holds (D11: nothing the model sends confirms an action; only the click);
//  - a header "<Title> helper" with New chat (D6: a fresh chat) and Open in
//    full chat (the same chat on the agent page, where no page tools are sent);
//  - Talk (T20) in the composer: what the person says is sent through the same
//    sendMessage, so it carries the page's tools too;
//  - the page's own rows (introRows: the Tables helper's opening summary),
//    merged into the chat by time. They need no chat: the chat is made when the
//    person first sends;
//  - the section's starter questions as pills above the composer until the
//    person's first message (the customer's list from System context, else the
//    shipped ones; starterQuestionsFor). A click sends it like a typed message;
//  - a greeting until the first message, as the pills: the time of day and the
//    person's first name, then the section's opening line (sectionIntroFor).
//    With intro rows (the Tables summary) the opening line follows them, as
//    the question that invites a follow-up (paneOpening). While the summary is
//    made the pane is locked and shows INTRO_LOADING_TITLE: no composer, Talk,
//    pills or opening line (#1908: two turns raced the summary).
//
// The pane is the content only; the host decides where it sits (a drawer
// beside Tables). The host passes the page's live tools (PageToolsProvider's
// livePageTools), its context for the agent, and the chat's transport.

import React, { useCallback, useRef } from 'react'
import { Box, Button, Typography } from '@mui/material'
import SidePaneChat, { type SidePaneConversation } from './SidePaneChat.js'
import type { ComposerModel } from './ComposerCard.js'
import { CARD_SX, PILL_RADIUS } from './surface.js'
import TalkButton from './TalkButton.js'
import DisabledReason from '../../components/system/DisabledReason'
import { mergeIntroRows } from './introRows.js'
import usePageAgent, { type PendingPageConfirm } from '../usePageAgent.js'
import { PAGE_HELPERS, helperSection } from '../pageHelpers.js'
import { sectionIntroFor, starterQuestionsFor } from '../../help/features'
import { paneOpening, timeOfDayGreeting, useFirstName } from '../../utils/greeting'
import { useHelpStarters } from '../../hooks/useHelpStarters'
import type { UseAgentSessionOptions } from '../useAgentSession.js'
import type { RenderPlugin } from '../plugins.js'
import type { AgentMessage, ArtifactInfo } from '../types.js'
import type { VoiceUITool } from '../../voice/types'
import { buildTranscribeEndpoint } from '../../hooks/useTranscribeEndpoint'
import useDictationAccess from '../../hooks/useDictationAccess'

export interface PageAgentPaneProps {
  /** A PAGE_HELPERS key, e.g. `tables`. */
  pageKey: string
  customer: string
  job: string
  /** The pane is showing: opening starts afresh (the chat is made at the first send) unless resumeKey is where it closed; closing answers held calls. */
  open: boolean
  /** Where the page is (the open table, the route): reopened at the same key, the chat carries on. The host keeps the pane mounted while closed. */
  resumeKey?: string
  onClose: () => void
  /** What is on the page now, for the agent. Read at each send. */
  getPageContext: () => string
  /** The page's live tools (PageToolsProvider's livePageTools). */
  getTools: () => VoiceUITool[]
  /** How the chat reaches the server: endpoints, auth, plugin event types. */
  sessionOptions?: UseAgentSessionOptions
  /** Render plugins for the agent's tables and charts (the chat page's own set). */
  plugins?: RenderPlugin[]
  /** The usual model picker's list. */
  models?: ComposerModel[]
  /** Open this chat on the agent page. Absent: a plain link to /agent/:customer/:sessionId. */
  onOpenFullChat?: (sessionId: string) => void
  /** Rows the page draws without a chat turn (the opening summary), merged in by time. */
  introRows?: AgentMessage[]
  /** The page is making an intro row: the pane shows it is busy and stays locked (no composer, Talk, pills or opening line) until the row arrives. */
  introLoading?: boolean
  /** The person may not chat here (AI access): the composer and Talk are disabled with this sentence. */
  composerDisabledReason?: string
  /** The profile the helper's chat is made with. Absent: `page-<pageKey>`. The Tables page names the Tables pane's below agent access (#1884). */
  profile?: string
  /** The person may not open the chat in the full agent (AI access): Open in full chat is disabled with this sentence, never hidden. */
  fullChatDisabledReason?: string
}

/** "set_quick_filter" → "Set quick filter". */
export function toolTitle(name: string): string {
  const words = name.replace(/_/g, ' ').trim()
  return words ? words[0].toUpperCase() + words.slice(1) : name
}

const argText = (v: unknown) => (typeof v === 'string' ? v : JSON.stringify(v))

const PILL_BUTTON_SX = { textTransform: 'none', fontSize: 12, borderRadius: PILL_RADIUS, px: 1.25, minWidth: 0 } as const

/** A held page call, waiting for the person. Shaped like AskUserCard. */
function PageConfirmCard({ call, onConfirm, onDecline }: { call: PendingPageConfirm; onConfirm: () => void; onDecline: () => void }) {
  const args = Object.entries(call.arguments ?? {}).filter(([k]) => k !== 'confirmed')
  return (
    <Box data-testid="page-confirm-card" role="group" aria-label={`Confirm ${toolTitle(call.name)}`} sx={{ ...CARD_SX, p: 2 }}>
      <Typography sx={{ fontSize: 11, color: 'text.secondary', mb: 0.25 }}>The helper wants to change the page</Typography>
      <Typography sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.8125rem' }}>{toolTitle(call.name)}</Typography>
      {call.meaning && (
        <Typography data-testid="page-confirm-meaning" sx={{ mt: 0.5, fontSize: '0.8125rem', color: 'text.primary' }}>
          {call.meaning}
        </Typography>
      )}
      {!call.meaning && call.description && (
        <Typography
          sx={{
            mt: 0.5, fontSize: 12, color: 'text.secondary', lineHeight: 1.4,
            display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}
        >
          {call.description}
        </Typography>
      )}
      {!call.meaning && args.length > 0 && (
        <Box component="dl" sx={{ m: 0, mt: 1, fontSize: 12, display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1, rowGap: 0.25 }}>
          {args.map(([k, v]) => (
            <React.Fragment key={k}>
              <Box component="dt" sx={{ color: 'text.secondary' }}>{k}</Box>
              <Box component="dd" sx={{ m: 0, fontFamily: 'monospace', wordBreak: 'break-word' }}>{argText(v)}</Box>
            </React.Fragment>
          ))}
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 1, mt: 1.5, justifyContent: 'flex-end' }}>
        <Button size="small" color="info" onClick={onDecline} sx={PILL_BUTTON_SX}>
          Decline
        </Button>
        <Button size="small" variant="contained" color="info" disableElevation onClick={onConfirm} sx={{ ...PILL_BUTTON_SX, px: 2 }}>
          Confirm
        </Button>
      </Box>
    </Box>
  )
}

/** The busy line while the page makes an intro row. The pane is locked until it arrives. */
export const INTRO_LOADING_TITLE = 'Generating a summary of the table, one moment.'

export default function PageAgentPane({
  pageKey, customer, job, open, resumeKey, onClose, getPageContext, getTools, sessionOptions, plugins, models, onOpenFullChat,
  introRows, introLoading, composerDisabledReason, profile, fullChatDisabledReason,
}: PageAgentPaneProps) {
  const agent = usePageAgent({ pageKey, customer, job, open, resumeKey, getPageContext, getTools, sessionOptions, profile })
  const s = agent.session
  const sessionId = s.session?.id ?? ''
  const title = PAGE_HELPERS.find(h => h.key === pageKey)?.title ?? pageKey
  const starters = useHelpStarters({ customer, enabled: open })
  // Dictation needs agent access and budget (audit C23); the Tables pane opens below it.
  const dictation = useDictationAccess(customer)
  // Pills until the person's first message; New chat (no messages) brings them back.
  const section = helperSection(pageKey)
  const asked = s.messages.some(m => m.role === 'user')
  const prompts = section && !asked && !introLoading ? starterQuestionsFor(section, starters.records).questions : undefined
  // The greeting goes with the pills. A section-less helper still says hello and the default line.
  const firstName = useFirstName()
  const hasIntroRows = Boolean(introRows?.length)
  const opening = asked ? undefined : paneOpening({
    greeting: timeOfDayGreeting(),
    firstName,
    // While the summary is made only the greeting shows; the opening line waits for it.
    intro: introLoading ? '' : sectionIntroFor(section ?? '', starters.records).intro,
    afterIntroRows: hasIntroRows,
  })

  // MessageRow needs stable callbacks (its memo); the latest session is read through refs.
  const sessionRef = useRef(s)
  sessionRef.current = s
  const onAnswerQuestion = useCallback((toolCallId: string, value: string) => sessionRef.current.markQuestionAnswered(toolCallId, value), [])
  const onSendAnswer = useCallback((content: string) => { void sessionRef.current.sendMessage(content) }, [])
  const idRef = useRef({ customer, sessionId })
  idRef.current = { customer, sessionId }
  // An artifact opens on the agent page in a new tab, so the page keeps its place.
  const onOpenPreview = useCallback((a: ArtifactInfo) => {
    const { customer: c, sessionId: id } = idRef.current
    if (!id) return
    const query = a.id ? `?artifact=${encodeURIComponent(a.id)}` : ''
    window.open(`/agent/${encodeURIComponent(c)}/${encodeURIComponent(id)}${query}`, '_blank', 'noopener')
  }, [])

  const conversation: SidePaneConversation = {
    messages: mergeIntroRows(s.messages, introRows),
    sessionId,
    isStreaming: s.isStreaming,
    activityStatus: s.activityStatus,
    artifacts: s.artifacts,
    renderedTables: s.renderedTables,
    renderedCharts: s.renderedCharts,
    askedQuestions: s.askedQuestions,
    createdDashboards: s.createdDashboards,
    plugins,
    pluginEvents: s.pluginEvents,
    apiBaseUrl: sessionOptions?.apiBaseUrl,
    authHeader: sessionOptions?.authHeader,
    onAnswerQuestion,
    onSendMessage: onSendAnswer,
    onOpenPreview,
  }

  // Talk sends through the page agent's sendMessage (the page's tools ride along).
  const talkSession = {
    session: s.session,
    messages: s.messages,
    isStreaming: s.isStreaming,
    error: s.error,
    askedQuestions: s.askedQuestions,
    queuedMessages: s.queuedMessages,
    sendMessage: (text: string) => s.sendMessage(text, s.selectedModel || undefined),
  }

  const fullChatHref = sessionId && !fullChatDisabledReason ? `/agent/${encodeURIComponent(customer)}/${encodeURIComponent(sessionId)}` : undefined
  const headerActions = (
    <>
      <Button size="small" color="info" onClick={() => { void agent.startNew() }} disabled={s.isCreating} sx={PILL_BUTTON_SX}>
        New chat
      </Button>
      {/* Below agent access the full agent is shown, disabled, with why and who to ask (#1884). */}
      <DisabledReason reason={fullChatDisabledReason}>
        <Button
          size="small"
          color="info"
          href={fullChatHref}
          disabled={!sessionId}
          onClick={onOpenFullChat ? (e: React.MouseEvent) => { e.preventDefault(); if (sessionId) onOpenFullChat(sessionId) } : undefined}
          sx={PILL_BUTTON_SX}
        >
          Open in full chat
        </Button>
      </DisabledReason>
    </>
  )

  const confirms = agent.pendingConfirms.length > 0 ? (
    <Box data-testid="page-confirm-cards" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {agent.pendingConfirms.map(c => (
        <PageConfirmCard key={c.callId} call={c} onConfirm={() => agent.confirm(c.callId)} onDecline={() => agent.decline(c.callId)} />
      ))}
    </Box>
  ) : undefined
  // The composer is disabled, never hidden: the sentence says why and who to ask.
  const refusal = composerDisabledReason ? (
    <Typography data-testid="page-composer-refusal" role="note" sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.4 }}>
      {composerDisabledReason}
    </Typography>
  ) : undefined
  const aboveComposer = confirms || refusal ? <>{confirms}{refusal}</> : undefined

  return (
    <SidePaneChat
      title={`${title} helper`}
      onClose={onClose}
      headerActions={headerActions}
      conversation={conversation}
      greeting={opening?.top}
      openingLine={opening?.after}
      loading={s.isStreaming || s.isCreating || !!introLoading}
      onStop={s.isStreaming && !s.isCreating ? () => { void agent.stop() } : undefined}
      loadingTitle={s.isCreating ? 'Starting a fresh chat…' : introLoading ? INTRO_LOADING_TITLE : undefined}
      error={s.error}
      onSend={(text, model) => s.sendMessage(text, model || undefined)}
      prompts={prompts}
      models={models}
      selectedModel={s.selectedModel}
      onModelChange={s.setSelectedModel}
      aboveComposer={aboveComposer}
      // No chat yet is fine: the first thing said makes it.
      composerLeading={<TalkButton session={talkSession} customer={customer} disabledReason={composerDisabledReason ?? (introLoading ? INTRO_LOADING_TITLE : undefined)} />}
      // The mic posts to goapi's /transcribe, metered to this customer and job,
      // with the chat's login (absent, dictation hit a bare /transcribe and failed).
      transcribeEndpoint={buildTranscribeEndpoint(customer, job)}
      dictationDisabledReason={dictation.reason}
      authHeader={sessionOptions?.authHeader}
      placeholder={`Ask the ${title} helper…`}
      disabled={!!composerDisabledReason || s.isCreating || !!introLoading}
    />
  )
}
